import { msg } from '@gonfalon/messages/msg';
import type { IconName } from '@launchpad-ui/icons';

import type { ModelProvider } from '#components/AiConfigs/AiConfigModelConfiguration/modelSchemas/aiConfigModelService';

import type { AiConfigMode, AiConfigModeTabId, EvaluatorKind } from './types';

export const chartBucketTypes = [
  'Costs',
  'Satisfaction',
  'Generations',
  'Tokens',
  'Time to first token',
  'Error rate',
  'Time to generate',
  'Evaluator metrics',
] as const;

export type ChartBucketType = (typeof chartBucketTypes)[number];

export const DEFAULT_MONITORING_METRIC_ID_COUNT = 3;

export const MAX_MONITORING_METRIC_ID_COUNT = 20;

export const defaultMonitoringMetricIds: ChartBucketType[] = ['Costs', 'Satisfaction', 'Generations'];

/** Search param holding the monitoring dashboard's selected chart bucket type. */
export const MONITORING_CHART_BUCKET_PARAM = 'metric';
export const AI_CONFIG_MONITORING_PREFS_STORAGE_KEY = 'ai-config-monitoring-preferences';

export function isChartBucketType(value: string | null | undefined): value is ChartBucketType {
  return value !== null && value !== undefined && (chartBucketTypes as readonly string[]).includes(value);
}

export const JUDGE_METRIC_ID_PREFIX = 'judge:';

export const CUSTOM_METRIC_ID_PREFIX = 'custom:';

export function toJudgeMetricId(evalKey: string): string {
  return `${JUDGE_METRIC_ID_PREFIX}${evalKey}`;
}

export function getJudgeEvalKeyFromMetricId(id: string): string | undefined {
  return id.startsWith(JUDGE_METRIC_ID_PREFIX) && id.length > JUDGE_METRIC_ID_PREFIX.length
    ? id.slice(JUDGE_METRIC_ID_PREFIX.length)
    : undefined;
}

export function toCustomMetricId(eventKey: string): string {
  return `${CUSTOM_METRIC_ID_PREFIX}${eventKey}`;
}

export function getCustomMetricEventKeyFromMetricId(id: string): string | undefined {
  return id.startsWith(CUSTOM_METRIC_ID_PREFIX) && id.length > CUSTOM_METRIC_ID_PREFIX.length
    ? id.slice(CUSTOM_METRIC_ID_PREFIX.length)
    : undefined;
}

export function isMonitoringMetricId(value: string | null | undefined): boolean {
  return (
    isChartBucketType(value) ||
    (typeof value === 'string' &&
      (getJudgeEvalKeyFromMetricId(value) !== undefined || getCustomMetricEventKeyFromMetricId(value) !== undefined))
  );
}

export function parseMonitoringMetricIdsParam(value: string | null | undefined): string[] {
  const parsedValues = value?.split(',').filter(isMonitoringMetricId) ?? [];
  return Array.from(new Set(parsedValues.length > 0 ? parsedValues : defaultMonitoringMetricIds)).slice(
    0,
    MAX_MONITORING_METRIC_ID_COUNT,
  );
}

export function serializeMonitoringMetricIdsParam(values: readonly string[]): string {
  return values.join(',');
}

export const chartTypes = [
  // Tokens
  'Tokens out (avg)',
  'Tokens in (avg)',
  'Tokens (avg)',
  // Satisfaction
  'Satisfaction rating',
  'Thumbs down on average',
  'Thumbs up on average',
  // Generations
  'Generations on average',
  // Latency
  'Time to generate',
  // Error rate
  'Error rate',
  'Time to 1st token (avg)',
  // Costs
  'Output cost',
  'Input cost',
  // Judge Metrics
  'Judge accuracy',
  'Judge relevance',
  'Judge toxicity',
] as const;

export const MonitoringDashboardChartTypeEnum = {
  TokensOut: 'Tokens out (avg)',
  TokensIn: 'Tokens in (avg)',
  Tokens: 'Tokens (avg)',
  Satisfaction: 'Satisfaction rating',
  ThumbsDown: 'Thumbs down on average',
  ThumbsUp: 'Thumbs up on average',
  Generations: 'Generations on average',
  Latency: 'Time to generate',
  ErrorRate: 'Error rate',
  TimeToFirstToken: 'Time to 1st token (avg)',
  OutputCost: 'Output cost',
  InputCost: 'Input cost',
  JudgeAccuracy: 'Judge accuracy',
  JudgeRelevance: 'Judge relevance',
  JudgeToxicity: 'Judge toxicity',
} as const;

export const iconNameByMode: Record<AiConfigMode, IconName> = {
  agent: 'robot',
  completion: 'chat-bubble-outline',
  judge: 'scale',
};

// A function, not a constant: `msg()` has to run at render time, after the messages provider mounts.
export function getModeDescription(mode: AiConfigMode): string {
  switch (mode) {
    case 'completion':
      return msg('aiConfigs.configModes.completionDescription');
    case 'agent':
      return msg('aiConfigs.configModes.agentDescription');
    case 'judge':
    default:
      return msg('aiConfigs.configModes.judgeDescription');
  }
}

export function getModeLabel(mode: AiConfigMode): string {
  switch (mode) {
    case 'completion':
      return msg('aiConfigs.configModes.completionLabel');
    case 'agent':
      return msg('aiConfigs.configModes.agentLabel');
    case 'judge':
    default:
      return msg('aiConfigs.configModes.judgeLabel');
  }
}

/**
 * Runtime allowlist of evaluator kinds, used to narrow the arbitrary `kind` string the pass-through
 * evaluations proxy can return. `satisfies` keeps the literal tuple type that narrowing relies on
 * while rejecting a member that isn't a real {@link EvaluatorKind}; a kind added to the API is
 * caught by `evaluatorKindLabelByKind` below, which must cover every kind.
 */
export const EVALUATOR_KINDS = ['deepeval', 'judge', 'scorer'] as const satisfies readonly EvaluatorKind[];

export const evaluatorKindLabelByKind: Record<EvaluatorKind, string> = {
  deepeval: 'DeepEval',
  judge: 'Judge',
  scorer: 'Scorer',
};

/**
 * Shared mode option definitions for AI Configs.
 * Used by both tabs and filter dialogs to ensure consistent mode handling.
 */
export type AiConfigModeOption = {
  id: AiConfigModeTabId;
  label: string;
};

export function getAiConfigModes(): readonly AiConfigModeOption[] {
  return [
    { id: 'all', label: msg('aiConfigs.configModes.allLabel') },
    { id: 'agent', label: msg('aiConfigs.configModes.agentLabel') },
    { id: 'completion', label: msg('aiConfigs.configModes.completionLabel') },
    { id: 'judge', label: msg('aiConfigs.configModes.judgeLabel') },
  ];
}

export const RESTRICTED_AI_CONFIG_KEY = 'new';

// Online Evaluation (Judges) constants
export { JUDGE_EVALUATION_METRIC_PREFIX as EDITABLE_EVAL_KEY_PREFIX } from '@gonfalon/rest-api/aiConfigMetricKeys';
export const EVENT_KEY_TOOLTIP = 'Identifies the metric used to record and display evaluation scores';
export const DESIRED_DIRECTION_TOOLTIP = 'Defines whether higher or lower scores indicate better results';
export const SAMPLING_RATE_TOOLTIP = 'Controls the percentage of responses evaluated';

type SuggestedModel = { name: string; description: string };

export const suggestedModels: Partial<Record<ModelProvider, SuggestedModel>> = {
  OpenAI: {
    name: 'gpt-5',
    description:
      "OpenAI's latest flagship model with state-of-the-art reasoning and tool-calling performance, ideal for complex multi-step agent workflows and long-horizon planning",
  },
  Anthropic: {
    name: 'claude-sonnet-4-5',
    description:
      "Anthropic's most advanced model, leading in agentic coding tasks with exceptional reasoning capabilities for sophisticated problem-solving and code generation",
  },
  Gemini: {
    name: 'gemini-1.5-pro-002',
    description:
      "Google's powerful reasoning model with strong performance on complex agent tasks, excellent for multi-step workflows requiring high accuracy and multimodal understanding",
  },
  Bedrock: {
    name: 'amazon.nova-2-pro-v1:0',
    description:
      "Amazon's most intelligent reasoning model designed specifically for agentic workflows, excelling at agentic coding, long-range planning, and sophisticated problem-solving",
  },
};

// more strictly typed signature always returns a suggested model
export function getSuggestedModel(
  provider: Extract<ModelProvider, 'OpenAI' | 'Anthropic' | 'Gemini' | 'Bedrock'>,
): SuggestedModel;
export function getSuggestedModel(provider: ModelProvider): SuggestedModel | undefined {
  return suggestedModels[provider];
}
